import { z } from '../zod';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { ThemeOverridesToolResponseValidationSchema } from '../theme/themeOverrides';
import {
  SharedEventAndFormResponseValidationSchema,
  SharedPageResponseValidationSchema,
} from '../page/get';
import { PageInvoiceSettingsInputValidationSchema } from '../page/common/invoiceSettings';
import { FilePurposeValidationSchema } from '../file/create';

export const FormPageFileResponseValidationSchema = z
  .object({
    id: z.string().meta({
      description: 'Unique identifier of the file.',
    }),
    name: z.string().nullish().meta({
      description: 'File name.',
    }),
    location: z.string().nullish().meta({
      description: 'URL of the file in storage.',
    }),
    purpose: FilePurposeValidationSchema.nullish().meta({
      description:
        'Purpose of the file — `image` for gallery images, `file` for downloadable files.',
    }),
    size: z.number().nullish().meta({
      description: 'File size in bytes.',
    }),
    type: z.string().nullish().meta({
      description: 'MIME type of the file (e.g. `image/png`, `application/pdf`).',
    }),
    width: z.number().nullish().meta({
      description: 'Image width in pixels. Only set for image files.',
    }),
    height: z.number().nullish().meta({
      description: 'Image height in pixels. Only set for image files.',
    }),
  })
  .meta({ id: 'FormPageFileResponse' });

export const FormPageResponseSchema = SharedPageResponseValidationSchema.extend(
  SharedEventAndFormResponseValidationSchema.shape,
)
  .extend({
    type: z.literal('form').meta({
      description: 'Type of page.',
    }),
    allowDynamicTitle: z.boolean().nullish().meta({
      description: 'Whether the title can be set dynamically via URL parameters.',
    }),
    allowDynamicDescription: z.boolean().nullish().meta({
      description: 'Whether the description can be set dynamically via URL parameters.',
    }),
    invoiceSettings: PageInvoiceSettingsInputValidationSchema.nullish().meta({
      description: 'Invoice configuration for manual payment flows.',
    }),
    files: z.array(FormPageFileResponseValidationSchema).nullish().meta({
      description:
        'Downloadable files attached to this form page, available to customers after submission.',
    }),
    submissionCount: z.number().nullish().meta({
      description: 'Number of form submissions.',
    }),
    formDetails: z
      .object({
        limitSubmissions: z
          .object({
            enabled: z.boolean().nullish(),
            enabledAt: z.string().nullish().meta({
              description: 'ISO 8601 timestamp of when duplicate-submission prevention was switched on.',
            }),
          })
          .nullish()
          .meta({
            description: 'Duplicate-submission prevention settings for this form.',
          }),
      })
      .nullish()
      .meta({
        description: 'Form behaviour settings.',
      }),
  })
  .meta({ id: 'FormPageResponse' });

// Only MCP reads these API payloads. It checks themeOverrides loosely, so a theme property the API starts
// exposing before the MCP worker redeploys can't fail its tools.
export const FormPageMcpPayloadSchema = FormPageResponseSchema.extend({
  themeOverrides: ThemeOverridesToolResponseValidationSchema,
});

export const CreateFormPageResponseValidationSchema = z.object({
  data: FormPageMcpPayloadSchema,
});

export const UpdateFormPageResponseValidationSchema = CreateFormPageResponseValidationSchema;

export const FormPageToolDataValidationSchema = FormPageMcpPayloadSchema.extend({
  hostedUrl: z.url().meta({
    description: 'The live URL where the form page can be accessed.',
  }),
  dashboardUrl: DashboardUrlField,
});

export const FormPageToolResponseValidationSchema = z.object({
  data: FormPageToolDataValidationSchema,
});

export type FormPageResponse = z.infer<typeof FormPageResponseSchema>;
export type CreateFormPageResponse = z.infer<typeof CreateFormPageResponseValidationSchema>;
export type FormPageToolData = z.infer<typeof FormPageToolDataValidationSchema>;
export type FormPageToolResponse = z.infer<typeof FormPageToolResponseValidationSchema>;
